import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import CvPageLayout from '../../../components/CvPageLayout';
import { Button, Form, Row, Col, Card, Alert } from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTrash, FaPlus, FaArrowLeft, FaArrowRight} from 'react-icons/fa';
import Loader from '../../../components/Loader';
import { fetchSections, updateSection } from '../../../actions/additionalSectionsActions';
import GradientButton from '../../../components/GradientButton';

const routesMap = {
  check_1: '/cv-career/cv-generator/section/professional-summary',
  check_2: '/cv-career/cv-generator/section/achievements',
  check_3: '/cv-career/cv-generator/section/associations',
  check_4: '/cv-career/cv-generator/section/links',
  check_5: '/cv-career/cv-generator/section/additional-info',
  check_6: '/cv-career/cv-generator/section/certificates',
  check_7: '/cv-career/cv-generator/section/custom',
};

export default function CvSectionTemplate({ currentId, description }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [items, setItems] = useState([]);
  const [newItem, setNewItem] = useState('');
  const [sectionDbId, setSectionDbId] = useState(null);
  const [backlink, setBacklink] = useState('/cv-career/cv-generator/section/bonus-info');
  const [nextlink, setNextlink] = useState('/cv-career/cv-generator/section/summary');

  const { sections, loading } = useSelector((state) => state.additionalSectionsList);
  const { loading: updating } = useSelector((state) => state.additionalSectionsUpdate);

  const [title, setTitle] = useState(''); 

  useEffect(() => {
    if (!sections?.results) return;

    const currentSection = sections.results.find((s) => s.section_type === currentId);
    if (currentSection) {
      setSectionDbId(currentSection.id);
      setTitle(currentSection.name || ''); 
      if (Array.isArray(currentSection.content)) {
        setItems(currentSection.content);
      } else if (typeof currentSection.content === 'string' && currentSection.content.trim() !== '') {
        setItems([currentSection.content]);
      } else {
        setItems([]);
      }
    }
  }, [sections, currentId]);

  useEffect(() => {
    if (!sections || sections.length === 0) {
      dispatch(fetchSections());
    }
  }, [dispatch, sections]);

  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem('selectedSections') || '[]');
    const idx = stored.indexOf(currentId);

    setBacklink(
      idx > 0
        ? routesMap[stored[idx - 1]] || '/cv-career/cv-generator/section/bonus-info'
        : '/cv-career/cv-generator/section/bonus-info'
    );

    setNextlink(
      idx >= 0 && idx < stored.length - 1
        ? routesMap[stored[idx + 1]] || '/cv-career/cv-generator/section/summary'
        : '/cv-career/cv-generator/section/summary'
    );
  }, [currentId]);

  const addItem = () => {
    if (newItem.trim()) {
      setItems((prev) => [...prev, newItem.trim()]);
      setNewItem('');
    }
  };

  const removeItem = (index) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const saveToBackend = async (redirectUrl) => {
    if (!sectionDbId) {
      navigate(redirectUrl);
      return;
    }

    const sectionToUpdate = {
      id: sectionDbId,
      section_type: currentId,
      content: items,
      ...(currentId === 'check_7' && { name: title }),
    };

    await dispatch(updateSection(sectionToUpdate));
    navigate(redirectUrl);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addItem();
    }
  };

  if (loading) {
    return (
      <CvPageLayout currentStep="Sekcje dodatkowe">
        <Loader />
      </CvPageLayout>
    );
  }

  return (
    <CvPageLayout currentStep="Sekcje dodatkowe">
      <div style={{
        minHeight: '100vh',
        paddingTop: '2rem',
        paddingBottom: '4rem',
        width: '100%'
      }}>
        <div className="container">
          <Row className="justify-content-center">
            <Col xs={12} lg={10} xl={8}>
              {/* HEADER */}
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="text-center mb-4"
              >
                <div style={{ fontSize: '3rem' }} className="mb-2">📋</div>
                <h2 className="text-white fw-bold mb-2">
                  {currentId === 'check_7' ? (title || 'Własna sekcja') : title}
                </h2>
                <p className="text-white-50">
                  {description}
                </p>
              </motion.div>

              {/* GŁÓWNA KARTA */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                <Card 
                  className="shadow-lg border-0"
                  style={{ borderRadius: '20px', overflow: 'hidden' }}
                >
                  {/* HEADER KARTY */}
                  <Card.Header 
                    style={{
                      background: 'linear-gradient(135deg, var(--bs-primary) 0%, #0056b3 100%)',
                      border: 'none',
                      padding: '1.5rem'
                    }}
                  >
                    <div className="text-white">
                      {currentId === 'check_7' ? (
                        <div>
                          <h5 className="mb-2 fw-bold">✨ Nazwa sekcji</h5>
                          <Form.Control
                            type="text"
                            placeholder="np. Zainteresowania"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            style={{
                              borderRadius: '10px',
                              border: '2px solid rgba(255,255,255,0.3)',
                              padding: '0.75rem',
                              background: 'rgba(255,255,255,0.1)',
                              color: 'white'
                            }}
                            className="text-white"
                          />
                        </div>
                      ) : (
                        <h5 className="mb-0 fw-bold">{title}</h5>
                      )}
                    </div>
                  </Card.Header>

                  <Card.Body className="p-4">
                    {/* INPUT DO DODAWANIA */}
                    <Form.Group className="mb-4">
                      <Form.Label className="fw-medium">
                        Dodaj element
                      </Form.Label>
                      <div className="d-flex gap-2">
                        <Form.Control
                          type="text"
                          placeholder="Dodaj element..."
                          value={newItem}
                          onChange={(e) => setNewItem(e.target.value)}
                          onKeyPress={handleKeyPress}
                          style={{
                            borderRadius: '10px',
                            border: '2px solid #e9ecef',
                            padding: '0.75rem'
                          }}
                        />
                        <Button
                          onClick={addItem}
                          style={{
                            borderRadius: '10px',
                            border: 'none',
                            background: 'linear-gradient(135deg, var(--bs-primary) 0%, #0056b3 100%)',
                            padding: '0.75rem 1.5rem',
                            minWidth: '60px'
                          }}
                        >
                          <FaPlus />
                        </Button>
                      </div>
                      <Form.Text className="text-muted">
                        <small>Naciśnij Enter lub kliknij + aby dodać</small>
                      </Form.Text>
                    </Form.Group>

                    {/* LISTA ELEMENTÓW */}
                    {items.length > 0 ? (
                      <div className="mb-4">
                        <h6 className="fw-bold mb-3">Dodane elementy:</h6>
                        <AnimatePresence>
                          {items.map((item, index) => (
                            <motion.div
                              key={index}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              exit={{ opacity: 0, x: 20 }}
                              transition={{ duration: 0.2 }}
                              className="mb-2"
                            >
                              <Card
                                className="border-0 shadow-sm"
                                style={{
                                  borderRadius: '10px',
                                  background: 'rgba(var(--bs-primary-rgb), 0.05)'
                                }}
                              >
                                <Card.Body className="p-3">
                                  <Row className="align-items-center">
                                    <Col>
                                      <div className="d-flex align-items-start">
                                        <span className="me-2 text-primary fw-bold">•</span>
                                        <span style={{ fontSize: '0.95rem' }}>{item}</span>
                                      </div>
                                    </Col>
                                    <Col xs="auto">
                                      <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() => removeItem(index)}
                                        style={{
                                          borderRadius: '8px',
                                          padding: '0.4rem 0.6rem'
                                        }}
                                      >
                                        <FaTrash size={14} />
                                      </Button>
                                    </Col>
                                  </Row>
                                </Card.Body>
                              </Card>
                            </motion.div>
                          ))}
                        </AnimatePresence>
                      </div>
                    ) : (
                      <Alert 
                        variant="info" 
                        className="border-0 text-center"
                        style={{ borderRadius: '15px' }}
                      >
                        <div style={{ fontSize: '2rem' }} className="mb-2">📝</div>
                        <small>
                          Dodaj przynajmniej jeden element do tej sekcji
                        </small>
                      </Alert>
                    )}
                  </Card.Body>
                </Card>
                <div className="row g-3 mt-3">
                  <div className="d-flex justify-content-between">
                    <GradientButton
                      onClick={() => saveToBackend(backlink)}
                      disabled={updating}
                      gradient="pink"
                    >
                      <FaArrowLeft className="me-2" />
                      {updating ? 'Zapisywanie...' : 'Wstecz'}                          
                    </GradientButton>

                    <GradientButton
                      onClick={() => saveToBackend(nextlink)}
                      disabled={updating}
                      gradient="blue"
                    >
                      {updating ? 'Zapisywanie...' : 'Dalej'}
                      <FaArrowRight className="ms-2" />
                    </GradientButton>
                  </div>
                </div>
              </motion.div>
            </Col>
          </Row>
        </div>
      </div>
    </CvPageLayout>
  );
}